import { useCallback } from "react";
import { createKeyword, setModuleTagPillar } from "../kpiGridApi";
import { keywordTypeFor } from "./helpers";

const PILLAR_NOT_SAVED = "Could not save the ESG pillar";

/**
 * The grid's writes to the keyword vocabulary: filing a new tag value, and putting a module tag under an ESG
 * pillar. Each keeps the vocabulary the cells read from in step with what the backend stored.
 *
 * @param {object} args
 * @param {Record<string, string[]>} args.tagValuesByKey
 * @param {(keywordType: string, keywordValue: string, esgPillar: string|null) => void} args.addKeyword
 * @param {(moduleTag: string, esgPillar: string) => void} args.setModulePillar
 * @param {(moduleTag: string, esgPillar: string) => void} args.applyPillar updates the pillar panel's listing
 * @returns {{ fileTagValue: (fieldKey: string, value: string, esgPillar?: string|null) => Promise<void>, setPillar: (moduleTag: string, esgPillar: string) => Promise<string|null> }}
 */
export function useKeywordWrites({ tagValuesByKey, addKeyword, setModulePillar, applyPillar }) {
  /**
   * Files a tag value under its field's keyword type. A module value carries the pillar picked for it; the keyword
   * that comes back says how the tag is spelt and which pillar it really has, since an existing spelling keeps its own.
   * That pillar is shown on every spelling and in the panel, also when the tag was already in the vocabulary.
   * A request that never reached the server files nothing.
   */
  const fileTagValue = useCallback(
    async (fieldKey, value, esgPillar = null) => {
      const keywordType = keywordTypeFor(tagValuesByKey, fieldKey);
      let res;
      try {
        res = await createKeyword(keywordType, value, { esgPillar });
      } catch {
        return;
      }
      if (!res?.isSuccess) return;
      const { keyword_value: storedValue, esg_pillar: storedPillar } = res.data.data;
      addKeyword(keywordType, storedValue, storedPillar);
      if (!storedPillar) return;
      setModulePillar(storedValue, storedPillar);
      applyPillar(storedValue, storedPillar);
    },
    [tagValuesByKey, addKeyword, setModulePillar, applyPillar],
  );

  /** Puts a module tag under a pillar and shows it everywhere; resolves to why it failed, or null. */
  const setPillar = useCallback(
    async (moduleTag, esgPillar) => {
      let res;
      try {
        res = await setModuleTagPillar(moduleTag, esgPillar);
      } catch {
        return PILLAR_NOT_SAVED;
      }
      if (!res?.isSuccess) return res?.data?.error || PILLAR_NOT_SAVED;
      setModulePillar(moduleTag, esgPillar);
      applyPillar(moduleTag, esgPillar);
      return null;
    },
    [setModulePillar, applyPillar],
  );

  return { fileTagValue, setPillar };
}
